import * as React from 'react';
import { JSX, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    FormGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { RowDataType, TableMetaData } from 'types/common/common';
import Menu from '@mui/material/Menu';
import pxToRem from 'assets/theme/function/px-to-rem';
import dayjs from 'dayjs';
import {
    TableSort,
    TableFooters,
    TableToolBar,
    TableBodyContent,
} from 'types/table/table-sort';
import { TableCellSorting } from 'component/flexible-table/sorting-table';
import { InitTableCellFooter } from 'component/flexible-table/footer-table';
import { InitToolBarItem } from 'component/flexible-table/tool-bar';
import { InitValueCell } from 'component/flexible-table/table-body';
import { ReactComponent as Plus } from 'assets/images/plus.svg';
import colors from 'assets/theme/base/colors';
import rgba from 'assets/theme/function/rgba';

interface Props {
    tableMetaDataList: TableMetaData[];
    setTableMetaDataList: React.Dispatch<React.SetStateAction<TableMetaData[]>>;
    summaryData?: { [key: string]: any };
    tableDataList: RowDataType[];
    sort?: string;
    setSort?: React.Dispatch<React.SetStateAction<string>>;
    rowClickFunction?: (rowData: RowDataType) => void;
    createNewFun?: any;
    buttonName?: string;
    sx?: any;
}

const FlexibleTable: React.FC<Props> = (props: Props) => {
    const {
        tableMetaDataList,
        tableDataList,
        summaryData,
        sort,
        setSort,
        rowClickFunction,
        createNewFun,
        buttonName,
    } = props;
    const [hiddenCells, setHiddenCells] = useState<string[]>([]);
    const [toolbarContent, setToolbarContent] = useState<TableToolBar[]>([]);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [maxHeight, setMaxHeight] = useState(window.innerHeight - 300);

    const open = Boolean(anchorEl);

    const cellId = 0;

    const sortData = (a: TableMetaData, b: TableMetaData) => {
        return a.position > b.position ? 1 : -1;
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const tableCellComponent = (
        cellValue: string,
        index: string,
        textMaxLength?: number,
    ): JSX.Element => {
        return textMaxLength ? (
            <Tooltip title={cellValue} followCursor key={index}>
                <TableCell
                    sx={{
                        whiteSpace: 'wrap',
                        overflow: 'hidden',
                    }}
                >
                    {cutCellText(cellValue, textMaxLength)}
                </TableCell>
            </Tooltip>
        ) : (
            <TableCell key={index}>{cellValue}</TableCell>
        );
    };

    const cutCellText = (cellText: string, maxLength: number) => {
        if (cellText.length <= maxLength) {
            return cellText;
        }
        const endTextPart = cellText.slice(maxLength);
        const firstSpaceIndex = endTextPart.indexOf(' ');

        return firstSpaceIndex !== -1
            ? cellText.slice(0, maxLength + firstSpaceIndex) + ' ... '
            : cellText;
    };

    const initializeData = useMemo(() => {
        tableMetaDataList.sort(sortData);
        const headContent: TableSort[] = [];
        const bodyContent: TableBodyContent[] = [];
        const footerContent: TableFooters[] = [];
        const initializedToolbarContent: TableToolBar[] = [];

        for (const data of tableMetaDataList) {
            initializedToolbarContent.push({
                dataKey: data.key,
                dataTitle: data.title,
            });
            if (hiddenCells.includes(data.key)) {
                continue;
            }
            headContent.push({
                title: data.title,
                key: data.key,
                isSortable: data.isSortable,
            });

            if (summaryData) {
                footerContent.push({ summaryFieldKey: data.summaryFieldKey });
            }
        }

        let rowValue;
        for (let i = 0; i < tableDataList.length; i++) {
            const cellComponentValues: JSX.Element[] = [];
            for (const tableMetaData of tableMetaDataList) {
                if (tableDataList[i].hasOwnProperty(tableMetaData.key)) {
                    if (
                        tableDataList[i][tableMetaData.key] &&
                        tableMetaData.type === 'date' &&
                        tableMetaData.format
                    ) {
                        rowValue = dayjs(
                            tableDataList[i][tableMetaData.key],
                        ).format(tableMetaData.format);
                    } else {
                        if (hiddenCells.includes(tableMetaData.key)) {
                            continue;
                        }
                        rowValue = tableDataList[i][tableMetaData.key];
                    }
                } else {
                    rowValue = '';
                }

                const cellComponentValue: JSX.Element = tableCellComponent(
                    rowValue,
                    tableMetaData.key,
                    tableMetaData.textMaxLength,
                );

                cellComponentValues.push(cellComponentValue);
            }

            bodyContent.push({
                cellComponentValues: cellComponentValues,
                i: i + 1,
                tableDataLists: tableDataList[i].rowComponent,
                tableDataList: tableDataList[i],
            });
        }

        setToolbarContent(initializedToolbarContent);

        return (
            <>
                <TableHead>
                    <TableRow>
                        {headContent.map((value: TableSort) => (
                            <TableCellSorting
                                key={value.key}
                                name={value.key}
                                sort={sort}
                                setSort={setSort}
                                cellId={cellId}
                                isSortable={value.isSortable}
                                value={value.title}
                            />
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody
                    sx={{
                        cursor: rowClickFunction ? 'pointer' : '',
                    }}
                >
                    {bodyContent?.map((value, index: number) => (
                        <InitValueCell
                            key={index}
                            rowClickFunction={rowClickFunction}
                            cellComponentValues={value.cellComponentValues}
                            index={value.i}
                            RowComponent={value.tableDataLists}
                            rowData={value.tableDataList}
                        />
                    ))}
                </TableBody>

                {tableDataList.length > 0 && footerContent.length > 0 && (
                    <TableFooter
                        sx={{
                            left: 0,
                            bottom: 0,
                            zIndex: 2,
                            position: 'sticky',
                        }}
                    >
                        <TableRow sx={{ textAlign: 'left' }}>
                            {footerContent.map(
                                (value: TableFooters, index: number) => (
                                    <InitTableCellFooter
                                        key={index}
                                        summaryFieldKey={
                                            value.summaryFieldKey ?? ''
                                        }
                                        cellId={index}
                                        summaryData={summaryData}
                                    />
                                ),
                            )}
                        </TableRow>
                    </TableFooter>
                )}
            </>
        );
    }, [tableMetaDataList, tableDataList, sort, hiddenCells]);

    useEffect(() => {
        const handleResize = () => setMaxHeight(window.innerHeight - 300);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <Box display={'flex'} justifyContent={'end'}>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                >
                    <FormGroup
                        aria-label="position"
                        row={false}
                        sx={{ display: 'flex' }}
                    >
                        {toolbarContent?.map((value, index) => (
                            <InitToolBarItem
                                key={index}
                                setHiddenCells={setHiddenCells}
                                dataKey={value.dataKey ?? ''}
                                dataTitle={value.dataTitle ?? ''}
                                hiddenCells={hiddenCells}
                            />
                        ))}
                    </FormGroup>
                </Menu>

                {buttonName ? (
                    <Button onClick={createNewFun} size={'small'}>
                        <Box
                            p={pxToRem(5)}
                            display={'flex'}
                            alignItems={'center'}
                        >
                            <Plus />
                            <Typography
                                pl={pxToRem(10)}
                                color={rgba(colors.primary.main, 1)}
                                variant={'subtitle1'}
                            >
                                {buttonName}
                            </Typography>
                        </Box>
                    </Button>
                ) : (
                    <Box p={pxToRem(5)} />
                )}
            </Box>

            <TableContainer
                sx={{
                    minHeight: pxToRem(300),
                    maxHeight: `${maxHeight}px`,
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader aria-label="a dense table">
                    {initializeData}
                </Table>
            </TableContainer>
        </>
    );
};

export default FlexibleTable;
